import { stageOperationalEvidence } from './operationalEvidenceOutbox.js';
import type { CapturedPhoto } from '../adapters/camera/index.js';
import { validateOfflineRegisterPatch, type OfflineRegisterTable } from '@trackeat/shared';
import { readPersistedSession } from '../auth/sessionStorage.js';
import { resolveActiveTenantId } from '../auth/effectiveTenant.js';
import { getActiveSharedTabletOperator, requireActiveOperatorAssertion } from '../auth/activeOperatorSession.js';
import { flushLocalDb, getDeviceId, openLocalDb, REGISTER_OWNER_FIELD } from '../db/local.js';
import { getSyncEngine } from '../sync/engine.js';
import { withMasterCacheSerialization } from '../sync/stockCommandSqlite.js';
import { SUPABASE_URL } from '../supabase.js';

export type { OfflineRegisterTable };
export interface RegisterScope { accountId: string; tenantId: string; businessId: string | null }
export interface RegisterDraft<T> { id: string; payload: T }
export interface RegisterSyncState {
  _syncStatus: 'pending' | 'rejected' | 'synced';
  _syncError: string | null;
}

/** This reads the SDK's validated persisted identity without refreshing offline.
 * AuthGate still owns access; every server write independently enforces RLS. */
function assertScope(scope: RegisterScope): void {
  const session = readPersistedSession(SUPABASE_URL);
  if (!session || session.user.id !== scope.accountId) throw new Error('La sessione è cambiata: riapri il registro');
  const encoded = session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const claims = JSON.parse(atob(encoded.padEnd(encoded.length + (4 - encoded.length % 4) % 4, '='))) as Record<string, unknown>;
  const tenant = resolveActiveTenantId(claims, Array.isArray(claims.tenants) ? claims.tenants as string[] : []);
  const business = typeof claims.active_business_id === 'string' ? claims.active_business_id : null;
  if (tenant !== scope.tenantId || business !== scope.businessId) throw new Error('Il ristorante attivo è cambiato: riapri il registro');
}

function draftKey(scope: RegisterScope, table: OfflineRegisterTable): string[] {
  return [scope.accountId, scope.tenantId, scope.businessId ?? '', table];
}

export async function readRegisterDraft<T>(scope: RegisterScope, table: OfflineRegisterTable): Promise<RegisterDraft<T> | null> {
  assertScope(scope);
  const db = await openLocalDb(null);
  const result = await db.query(`SELECT payload FROM work_draft_cache_v1 WHERE account_id = ? AND tenant_id = ?
    AND business_id = ? AND resource_kind = 'haccp_register' AND resource_key = ?`, draftKey(scope, table));
  assertScope(scope);
  const row = result.values?.[0] as { payload: string } | undefined;
  if (!row) return null;
  const draft = JSON.parse(row.payload) as RegisterDraft<T>;
  if (typeof draft.id !== 'string' || !draft.payload || typeof draft.payload !== 'object') {
    throw new Error('Bozza locale non leggibile: non è stata sovrascritta');
  }
  return draft;
}

export async function writeRegisterDraft(scope: RegisterScope, table: OfflineRegisterTable, draft: RegisterDraft<unknown>): Promise<void> {
  await withMasterCacheSerialization(async () => {
    assertScope(scope);
    const db = await openLocalDb(null);
    assertScope(scope);
    await db.run(`INSERT INTO work_draft_cache_v1(account_id,tenant_id,business_id,resource_kind,resource_key,payload,updated_at)
      VALUES (?,?,?,'haccp_register',?,?,?)
      ON CONFLICT(account_id,tenant_id,business_id,resource_kind,resource_key)
      DO UPDATE SET payload=excluded.payload,updated_at=excluded.updated_at`,
    [...draftKey(scope, table), JSON.stringify(draft), new Date().toISOString()]);
    await flushLocalDb({ required: true });
    assertScope(scope);
  });
}

export async function deleteRegisterDraft(scope: RegisterScope, table: OfflineRegisterTable): Promise<void> {
  await withMasterCacheSerialization(async () => {
    assertScope(scope);
    const db = await openLocalDb(null);
    const previous = (await db.query(`SELECT payload,updated_at FROM work_draft_cache_v1 WHERE account_id = ? AND tenant_id = ?
      AND business_id = ? AND resource_kind = 'haccp_register' AND resource_key = ?`, draftKey(scope, table))).values?.[0] as
      { payload: string; updated_at: string } | undefined;
    assertScope(scope);
    if (!previous) return;
    await db.run(`DELETE FROM work_draft_cache_v1 WHERE account_id = ? AND tenant_id = ?
      AND business_id = ? AND resource_kind = 'haccp_register' AND resource_key = ?`, draftKey(scope, table));
    try {
      await flushLocalDb({ required: true });
    } catch (error) {
      // A failed discard must remain readable in this process too. The same
      // serialization boundary prevents a newer draft from being overwritten.
      await db.run(`INSERT OR REPLACE INTO work_draft_cache_v1(account_id,tenant_id,business_id,resource_kind,resource_key,payload,updated_at)
        VALUES (?,?,?,'haccp_register',?,?,?)`, [...draftKey(scope, table), previous.payload, previous.updated_at]);
      await flushLocalDb();
      throw error;
    }
    assertScope(scope);
  });
}

export async function saveOfflineRegister(input: {
  scope: RegisterScope; table: OfflineRegisterTable; id: string; patch: Record<string, unknown>;
}): Promise<void> {
  const { scope, table, id } = input;
  assertScope(scope);
  requireActiveOperatorAssertion();
  const operator = getActiveSharedTabletOperator();
  const quickRegister = ['pest_control_logs', 'oil_control_logs', 'acrylamide_control_logs'].includes(table);
  const actor = operator ? operator.accountUserId ?? (quickRegister ? scope.accountId : null) : scope.accountId;
  if (!actor) throw new Error('L’operatore attivo non è autorizzato a compilare questo registro');
  const actorField = table === 'non_conformities' ? 'rilevata_da_user_id'
    : table === 'food_transports' ? 'operatore_user_id' : 'registrato_da_user_id';
  const { _photo, ...fields } = input.patch;
  const photo = _photo as (CapturedPhoto & { id: string }) | null | undefined;
  const candidate = {
    ...fields,
    ...(quickRegister ? { operatore_personnel_id: operator?.tenantPersonnelId ?? null, operatore_nome: operator?.fullName ?? fields.operatore_nome ?? null } : {}), id, tenant_id: scope.tenantId, business_id: scope.businessId, [actorField]: actor,
  };
  const assertUnchanged = (): void => {
    assertScope(scope);
    if (getActiveSharedTabletOperator() !== operator) throw new Error('L’operatore è cambiato: riapri il registro');
  };
  if (photo) {
    if (!quickRegister || !scope.businessId) throw new Error('Scegli la sede prima di allegare una foto');
    const evidence = await stageOperationalEvidence({ tenantId: scope.tenantId, businessId: scope.businessId, kind: 'register', targetId: id, eventClientUuid: photo.id, photo });
    assertUnchanged();
    (candidate as Record<string, unknown>).foto_storage_path = evidence.storage_path;
  }
  const patch = validateOfflineRegisterPatch(table, candidate);
  const deviceId = await getDeviceId();
  assertUnchanged();
  await getSyncEngine().queueMaster({
    op: 'insert', table, id, patch, base_version: 0, device_id: deviceId, client_ts: new Date().toISOString(),
  }, { ...scope, assertScope: assertUnchanged });
}

/** Pending and rejected entries remain visible only to the account that saved
 * them. Server-confirmed rows keep the existing tenant/business read scope. */
export async function listOfflineRegisterRows<T>(scope: RegisterScope, table: OfflineRegisterTable): Promise<Array<T & RegisterSyncState>> {
  return withMasterCacheSerialization(async () => {
    assertScope(scope);
    const db = await openLocalDb(null);
    const masters = await db.query(`SELECT row FROM masters_local WHERE table_name = ? AND tenant_id = ? AND archived = 0`, [table, scope.tenantId]);
    const pending = await db.query(`SELECT payload,last_error FROM pending_ops WHERE kind = 'master'`);
    const rejected = await db.query(`SELECT payload_json AS payload,reason AS last_error FROM pending_op_reconciliations_v1
      WHERE kind = 'master' AND status = 'reconciliation_required'`);
    const state = new Map<string, { owner: unknown; status: 'pending' | 'rejected'; error: string | null; patch: Record<string, unknown> }>();
    for (const [result, status] of [[pending, 'pending'], [rejected, 'rejected']] as const) {
      for (const raw of result.values ?? []) {
        const record = raw as { payload: string; last_error: string | null };
        let op: { table?: string; id?: string; patch?: Record<string, unknown>; [REGISTER_OWNER_FIELD]?: unknown };
        try { op = JSON.parse(record.payload) as typeof op; } catch { continue; }
        if (op.table === table && op.id && op.patch) state.set(op.id, { owner: op[REGISTER_OWNER_FIELD], status, error: record.last_error, patch: op.patch });
      }
    }
    const rows = new Map<string, Record<string, unknown>>();
    for (const raw of masters.values ?? []) {
      const row = JSON.parse((raw as { row: string }).row) as Record<string, unknown>;
      rows.set(String(row.id), row);
    }
    // A pull may contain a conflicting server row. The durable outbox/ledger
    // remains the source of the user's pending or rejected intent.
    for (const [id, local] of state) rows.set(id, { ...local.patch, id });
    const result: Array<T & RegisterSyncState> = [];
    for (const row of rows.values()) {
      if (row.tenant_id !== scope.tenantId || (row.business_id ?? null) !== scope.businessId) continue;
      const local = state.get(String(row.id));
      const owner = local?.owner ?? row[REGISTER_OWNER_FIELD];
      if (owner !== undefined && owner !== scope.accountId) continue;
      delete row[REGISTER_OWNER_FIELD];
      result.push({ ...row, _syncStatus: local?.status ?? 'synced', _syncError: local?.error ?? null } as T & RegisterSyncState);
    }
    assertScope(scope);
    return result;
  });
}
